import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Editor from '@monaco-editor/react';
import { useTheme } from '@/providers/ThemeProvider';
import type { ReplayBundleV1 } from '@arrowise/shared';

interface CodeViewerProps {
    bundle: ReplayBundleV1;
    selectedIdentity: string | undefined;
    currentFileContent: string;
    t: number;
}

export default function CodeViewer({ bundle, selectedIdentity, currentFileContent, t }: CodeViewerProps) {
    const { resolvedTheme } = useTheme();
    const monacoTheme = resolvedTheme === 'dark' ? 'vs-dark' : 'vs';

    function formatMs(ms: number): string {
        const total = Math.floor(ms / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
    }

    const getFileName = () => {
        if (!bundle || !selectedIdentity) return 'No file selected';
        const e = (bundle.files.catalog || []).find(f => {
            const ext = f as any;
            const id = (ext.identity || f.id || ext.clientFileId || f.filename) as string;
            return id === selectedIdentity;
        });
        if (!e) return 'No file selected';
        let name = e.filename;
        const ext = e as any;
        if (ext.names && Array.isArray(ext.names) && ext.names.length > 0) {
            const win = ext.names.find((n: any) => n && n.startMs <= t && (typeof n.endMs === 'undefined' || Number(n.endMs) > t));
            if (win && win.filename) name = String(win.filename);
        }
        return name;
    };

    return (
        <Card className="col-span-6 flex flex-col">
            <CardHeader>
                <CardTitle className="text-sm">{getFileName()}</CardTitle>
                <CardDescription>Read-only snapshot at {formatMs(t)}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="border rounded-md overflow-hidden h-[58vh]">
                    <Editor
                        height="100%"
                        defaultLanguage="javascript"
                        theme={monacoTheme}
                        options={{ readOnly: true, minimap: { enabled: false }, scrollBeyondLastLine: false }}
                        value={currentFileContent}
                    />
                </div>
            </CardContent>
        </Card>
    );
}
